// src/components/admin/TransportProfile.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import { AdminShell } from './Sidebar';
import { LoadingState } from '../ui/CrmPage';

export default function TransportProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState('');

  const [v, setV] = useState({
    id: '', name: '', registration_number: '', document: '',
    id_organization: '', id_division: '', is_deleted: false,
  });
  const [orgs, setOrgs] = useState([]);
  const [divs, setDivs] = useState([]);

  const orgMap = useMemo(() => { const m = {}; orgs.forEach((o) => { m[String(o.id)] = o.name; }); return m; }, [orgs]);
  const divMap = useMemo(() => { const m = {}; divs.forEach((d) => { m[String(d.id)] = d.name; }); return m; }, [divs]);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        setLoading(true); setError('');
        const r = await fetch(`${apiBase}v1/vehicles/${id}`, { signal: ctrl.signal });
        if (!r.ok) {
          const t = await r.text().catch(() => '');
          throw new Error(`HTTP ${r.status} ${r.statusText} ${t}`.trim());
        }
        const j = await r.json();
        setV({
          id: String(j.id ?? id),
          name: j.name ?? '',
          registration_number: j.registration_number ?? '',
          document: j.document ?? '',
          id_organization: j.id_organization ?? '',
          id_division: j.id_division ?? '',
          is_deleted: Boolean(j.is_deleted),
        });

        const [orgRes, divRes] = await Promise.all([
          fetch(`${apiBase}v1/organizations`, { signal: ctrl.signal }).catch(() => null),
          fetch(`${apiBase}v1/divisions`, { signal: ctrl.signal }).catch(() => null),
        ]);
        if (orgRes?.ok) {
          const data = await orgRes.json();
          setOrgs(Array.isArray(data) ? data : []);
        }
        if (divRes?.ok) {
          const data = await divRes.json();
          setDivs(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Ошибка загрузки');
      } finally { setLoading(false); }
    })();
    return () => ctrl.abort();
  }, [apiBase, id]);

  const onChange = (e) => { const { name, value } = e.target; setV((s) => ({ ...s, [name]: value })); };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(''); setSaveOk('');
    try {
      if (!v.name.trim()) throw new Error('Укажите название');
      if (!v.registration_number.trim()) throw new Error('Укажите гос. номер');
      const body = {
        name: v.name,
        registration_number: v.registration_number,
        document: v.document || null,
        id_organization: v.id_organization === '' ? null : Number(v.id_organization),
        id_division: v.id_division === '' ? null : Number(v.id_division),
        is_deleted: Boolean(v.is_deleted),
      };
      const r = await fetch(`${apiBase}v1/vehicles/${v.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`Save: HTTP ${r.status} ${r.statusText} ${t}`.trim());
      }
      setSaveOk('Изменения сохранены');
    } catch (e) {
      setSaveError(e.message || 'Не удалось сохранить');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <AdminShell title="Карточка ТС" subtitle="Загрузка…">
        <div className="crmpage crmpage--embedded">
          <div className="crmpage__main">
            <LoadingState text="Загружаем данные…" />
          </div>
        </div>
      </AdminShell>
    );
  }
  if (error) {
    return (
      <AdminShell title="Карточка ТС" subtitle="Ошибка">
        <div className="crmpage crmpage--embedded">
          <div className="crmpage__main">
            <div className="crmpage__alert crmpage__alert--error">{error}</div>
            <div className="adminform__actions">
              <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Назад</button>
            </div>
          </div>
        </div>
      </AdminShell>
    );
  }

  const orgName = orgMap[String(v.id_organization)] || (v.id_organization ? String(v.id_organization) : '—');
  const divName = divMap[String(v.id_division)] || (v.id_division ? String(v.id_division) : '—');

  return (
    <AdminShell
      breadcrumbs="Транспорт"
      title={v.name || 'Карточка ТС'}
      subtitle={`ID: ${v.id}`}
      actions={
        <>
          <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Назад</button>
          <button type="button" className="adminbtn adminbtn--primary" disabled={saving} onClick={onSave}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          {!!saveError && <div className="crmpage__alert crmpage__alert--error">{saveError}</div>}
          {!!saveOk && <div className="crmpage__alert" style={{ background: 'var(--success-bg)', borderColor: 'var(--success-border)', color: '#0F5B3C' }}>{saveOk}</div>}

          <div className="adminprofile__header">
            <div className="adminprofile__avatar">
              <LocalShippingIcon sx={{ fontSize: 26, color: '#fff' }} />
            </div>
            <div className="adminprofile__head-text">
              <h2 className="adminprofile__name">{v.name || 'Без названия'}</h2>
              <div className="adminprofile__meta">
                <span className="crmpage__status crmpage__status--brand">{v.registration_number || '—'}</span>
                <span className="adminprofile__meta-sep" />
                <span>{orgName}</span>
                <span className="adminprofile__meta-sep" />
                <span>{divName}</span>
                <span className="adminprofile__meta-sep" />
                <span className={`crmpage__status crmpage__status--${v.is_deleted ? 'neutral' : 'success'}`}>
                  {v.is_deleted ? 'Удалено' : 'Активно'}
                </span>
              </div>
            </div>
          </div>

          <form className="adminform" onSubmit={onSave}>
            <section className="adminform__card">
              <div className="adminform__section-title">Основное</div>
              <div className="adminform__grid">
                <label className="adminform__field adminform__field--full">
                  <span className="adminform__label">Название<span className="req">*</span></span>
                  <input className="adminform__input" name="name" value={v.name} onChange={onChange} required />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Гос. номер<span className="req">*</span></span>
                  <input className="adminform__input" name="registration_number" value={v.registration_number} onChange={onChange} required />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Документ</span>
                  <input className="adminform__input" name="document" value={v.document} onChange={onChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Организация</span>
                  <select className="adminform__select" name="id_organization" value={String(v.id_organization ?? '')} onChange={onChange}>
                    <option value="">— не выбрано —</option>
                    {orgs.map((o) => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                  </select>
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Подразделение</span>
                  <select className="adminform__select" name="id_division" value={String(v.id_division ?? '')} onChange={onChange}>
                    <option value="">— не выбрано —</option>
                    {divs.map((d) => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
                  </select>
                </label>
              </div>
            </section>
          </form>
        </div>
      </div>
    </AdminShell>
  );
}
